import { ReactElement } from 'react'

import FormLayout from 'components/v2/FormLayout'
import { FormattedMessage, defineMessages, useIntl } from 'react-intl'
import Heading from 'components/v2/Heading'
import { Row } from 'components/v2/Row'
import { Column } from 'components/v2/Column'
import { Input } from 'components/Input'
import { useFormikContext } from 'formik'
import { INewPlaceForm } from 'features/Places/types'
import { CityAutocomplete } from 'features/Cities/components/CityAutocomplete'
import { getParamValue } from 'features/Places/utils'
import { Textarea } from 'components/v2/Textarea'
import { FilePreview, InputFile } from 'components/v2/InputFile'
import { getFileName } from 'utils/file'

const messages = defineMessages({
  cityPlaceholder: {
    id: 'places.new.form.city.placeholder',
    defaultMessage: 'City'
  },
  line1Placeholder: {
    id: 'places.new.form.address.line1.placeholder',
    defaultMessage: 'Street'
  },
  line2Placeholder: {
    id: 'places.new.form.address.line2.placeholder',
    defaultMessage: 'Additional information'
  },
  zipCodePlaceholder: {
    id: 'places.new.form.address.zipCode.placeholder',
    defaultMessage: 'ZIP code'
  }
})

export const AddressFormCard = ({ isEdit }: { isEdit?: boolean }): ReactElement => {
  const intl = useIntl()
  const { values, handleChange, setFieldValue, touched, errors, handleBlur } = useFormikContext<INewPlaceForm>()

  const handleCityChange = async (value: TSFixMe): Promise<void> => {
    await setFieldValue('city', value)
    await setFieldValue('address.countryCode', value.countryCode)

    setFieldValue('param', getParamValue(values.name, value))
  }

  return (
    <FormLayout.Card small>
      <Heading.H4 small>
        <FormattedMessage id="places.form.section.address.title" defaultMessage="Address" />
      </Heading.H4>
      <Row noMargin>
        <Column width="50%">
          <Input
            id="address.line1"
            onChange={handleChange}
            onBlur={handleBlur}
            value={values.address.line1}
            placeholder={intl.formatMessage(messages.line1Placeholder)}
            touched={touched.address && touched.address.line1}
            errors={errors.address && errors.address.line1}
            label={<FormattedMessage id="places.new.form.address.line1.label" defaultMessage="Street" />}
          />
        </Column>
        <Column width="50%">
          <Input
            id="address.line2"
            onChange={handleChange}
            onBlur={handleBlur}
            value={values.address.line2}
            placeholder={intl.formatMessage(messages.line2Placeholder)}
            touched={touched.address && touched.address.line2}
            errors={errors.address && errors.address.line2}
            label={
              <FormattedMessage
                id="places.new.form.address.line2.label"
                defaultMessage="Additional information"
                values={{
                  small: (msg): TSFixMe => <small>{msg}</small>
                }}
              />
            }
            optional
          />
        </Column>
      </Row>
      <Row noMargin>
        <Column width="50%">
          <Row>
            <Column>
              <Input
                id="address.zipCode"
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.address.zipCode}
                placeholder={intl.formatMessage(messages.zipCodePlaceholder)}
                touched={touched.address && touched.address.zipCode}
                errors={errors.address && errors.address.zipCode}
                label={<FormattedMessage id="places.new.form.address.zipCode.label" defaultMessage="ZIP code" />}
              />
            </Column>
            <Column>
              <CityAutocomplete
                id="city.name"
                value={values.city.name}
                onChange={handleCityChange}
                onBlur={handleBlur}
                placeholder={intl.formatMessage(messages.cityPlaceholder)}
                touched={touched.city && touched.city.name}
                errors={errors.city && errors.city.name}
                label={<FormattedMessage id="places.new.form.city.label" defaultMessage="City" />}
                disabled={isEdit}
              />
            </Column>
          </Row>
        </Column>
      </Row>
      <Row>
        <Column width="50%">
          <Textarea
            id="directions"
            value={values.directions}
            onChange={handleChange}
            label={
              <FormattedMessage
                id="places.new.form.indications"
                defaultMessage="Indications"
                values={{
                  small: (msg): TSFixMe => <small>{msg}</small>
                }}
              />
            }
            optional
          />
        </Column>
        <Column width="50%">
          <InputFile
            label={<FormattedMessage id="places.new.form.photo.label" defaultMessage="Photo" />}
            onChange={(file): void => {
              setFieldValue('photo.file', file)
              setFieldValue('photo.name', file ? getFileName(file) : '')
            }}
            accept={{
              'image/jpeg': [],
              'image/jpg': [],
              'image/png': []
            }}
            optional
          />
          {(values.photo.file || values.photo.path) && (
            <FilePreview
              file={values.photo.file || (values.photo.path as string)}
              onRemoveFile={(): void => {
                setFieldValue('photo.file', null)
                setFieldValue('photo.name', '')
                setFieldValue('photo.path', '')
              }}
            />
          )}
        </Column>
      </Row>
    </FormLayout.Card>
  )
}
